import React, { useState, useEffect, useMemo, useCallback } from "react";
import clsx from "clsx";
import { useTheme } from "@mui/styles";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import TimelineIcon from "@mui/icons-material/Timeline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CallMissedOutgoingIcon from "@mui/icons-material/CallMissedOutgoing";
import BlockIcon from "@mui/icons-material/Block";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsIcon from "@mui/icons-material/Settings";
import FilterListIcon from "@mui/icons-material/FilterList";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import ListItem from "@mui/material/ListItem";
import Collapse from "@mui/material/Collapse";
import ListItemIcon from "@mui/material/ListItemIcon";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import ListItemText from "@mui/material/ListItemText";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AssignmentIcon from "@mui/icons-material/Assignment";

import { useUser } from "../../auth/UserProvider";
import { closeSidebar, openSidebar } from "../../store/ducks/duckSidebar";
import { useStylesLayout } from "../../styles/Layout";
import { MANAGEMENT_HR } from "../../constants/general";
import { setAbleToManagementHr } from "../../store/ducks/duckAuth";

import UserAccount from "./UserAccount";
import SidebarItem from "./SidebarItem";

export default function SideBar() {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const [initialHome, setInitialHome] = useState(true);
  const dispatch = useDispatch();
  const data = useSelector((state) => state.sidebar.data);
  const dataCollection = useSelector((state) => state.sidebarCollection.data);
  const { isAdmin } = useUser();
  const isSidebarOpen = useSelector((state) => state.sidebar.open);
  const loading = useSelector((state) => state.sidebar.isLoading);
  const [openAdministration, setOpenAdministration] = useState(false);
  const [openCollection, setOpenCollection] = useState(false);
  const [selectedName, setSelectedName] = useState("");
  const [isManagementHR, setIsManagementHr] = useState(false);
  const [isSidebarNavigating, setIsSidebarNavigating] = useState(false);
  const classes = useStylesLayout();
  const handleClickAdministration = () => setOpenAdministration(!openAdministration);

  const handleClickCollection = () => setOpenCollection(!openCollection);
  const isSidebarLoading = loading || isSidebarNavigating;

  const dataWithMstrInEconomics = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];

    const mapNode = (node) => {
      if (!node || typeof node !== "object") return node;

      const children = Array.isArray(node.childrens) ? node.childrens : [];
      const mappedChildren = children.map(mapNode);
      const nodeName = String(node.name || "");

      if (/conomics/i.test(nodeName)) {
        return {
          ...node,
          isInternal: true,
          internalLink: "mstr/embedding",
          childrens: [],
        };
      }

      return mappedChildren !== children ? { ...node, childrens: mappedChildren } : node;
    };

    return data.map(mapNode);
  }, [data]);

  const handleNavigateStart = useCallback((item) => {
    const isMstrNavigation =
      String(item?.internalLink || "")
        .toLowerCase()
        .startsWith("mstr") ||
      String(item?.name || "")
        .trim()
        .toUpperCase() === "MSTR";

    if (isMstrNavigation) {
      setIsSidebarNavigating(true);
    }
  }, []);

  const handleSidebarClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    dispatch(closeSidebar());
  };
  const handleRedirect = (redirectTo) => (e) => {
    e.preventDefault();
    history.push(redirectTo);
    dispatch(closeSidebar());
  };

  useEffect(() => {
    if (initialHome && location.pathname === "/") {
      dispatch(openSidebar());
      setInitialHome(false);
    }
  }, [dispatch, initialHome, location.pathname]);

  useEffect(() => {
    if (data && data.length > 0) {
      const isAbleToManagementHr = data.some((item) => item.name.toUpperCase() === MANAGEMENT_HR);

      dispatch(setAbleToManagementHr(isAbleToManagementHr));
      setIsManagementHr(isAbleToManagementHr);
    }
  }, [data, dispatch]);

  useEffect(() => {
    setIsSidebarNavigating(false);
  }, [location.pathname]);

  return (
    <Drawer
      BackdropProps={{ color: "white" }}
      PaperProps={{
        style: {
          backgroundColor: theme.palette.primary.main,
          color: "white",
        },
      }}
      sx={{
        "& .MuiList-root": {
          paddingTop: 0,
          paddingBottom: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          width: "100%",
        },
        "& .sidebar-click-item": {
          minHeight: 34,
          paddingTop: 2,
          paddingBottom: 2,
          paddingLeft: 8,
          paddingRight: 8,
          width: "100%",
          maxWidth: "100%",
          alignSelf: "stretch",
          borderRadius: 6,
          marginLeft: 8,
          marginRight: 8,
        },
        "& .MuiListItem-root": {
          paddingTop: 2,
          paddingBottom: 2,
          width: "100%",
          maxWidth: "100%",
        },
        "& .MuiListItemIcon-root": {
          minWidth: 32,
        },
        "& .MuiListItemText-root": {
          flex: "1 1 auto",
          marginTop: 0,
          marginBottom: 0,
          textAlign: "left",
        },
        "& .MuiListItemText-primary": {
          fontSize: "0.875rem",
          lineHeight: 1.2,
          textAlign: "left",
        },
      }}
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: isSidebarOpen,
        [classes.drawerClose]: !isSidebarOpen,
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: isSidebarOpen,
          [classes.drawerClose]: !isSidebarOpen,
        }),
      }}
      open={isSidebarOpen}
    >
      <div className={classes.toolbar}>
        <UserAccount fullWidth />
        <IconButton
          sx={{
            backgroundColor: "transparent",
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.12)",
            },
          }}
          onClick={handleSidebarClose}
        >
          {theme.direction === "rtl" ? (
            <ChevronRightIcon style={{ color: "white" }} />
          ) : (
            <ChevronLeftIcon style={{ color: "white" }} />
          )}
        </IconButton>
      </div>
      {isSidebarLoading && (
        <CircularProgress style={{ marginLeft: "42%", marginBottom: "20%", marginTop: "20%" }} />
      )}
      <Divider className={classes.divider} />
      <ListItem
        dense
        disableGutters
        onClick={handleClickCollection}
        sx={{ width: "100%", maxWidth: "100%", pl: 0 }}
      >
        <ListItemIcon style={{ color: "white" }}>
          <CallMissedOutgoingIcon />
        </ListItemIcon>
        <ListItemText
          className={classes.text}
          primary={"Collections"}
          style={{ cursor: "pointer" }}
        />
        {openCollection && isSidebarOpen ? (
          <ExpandLessIcon className={classes.icon} />
        ) : (
          <ExpandMoreIcon className={classes.icon} />
        )}
      </ListItem>

      <Collapse
        unmountOnExit
        in={openCollection && isSidebarOpen}
        style={{ marginLeft: 0, marginBottom: 0, minHeight: "auto", width: "100%" }}
        timeout={0}
      >
        <List disablePadding component="div" sx={{ width: "100%" }}>
          {!isSidebarLoading &&
            dataCollection &&
            dataCollection.length > 0 &&
            dataCollection.map((item) => {
              if (item.isCollection) {
                return (
                  <SidebarItem
                    key={item.name}
                    {...item}
                    dataCollection-cy={item.name}
                    isRoot={true}
                    selectedName={selectedName}
                    setSelectedName={setSelectedName}
                  />
                );
              }
            })}
          {!isSidebarLoading && dataCollection && dataCollection.length === 0 && (
            <ListItem>
              <ListItemIcon style={{ color: "white" }}>
                <BlockIcon />
              </ListItemIcon>
              <ListItemText className={classes.text} primary={"No collections loaded"} />
            </ListItem>
          )}
        </List>
      </Collapse>

      <Divider className={classes.divider} />
      <List>
        {!isSidebarLoading &&
          dataWithMstrInEconomics &&
          dataWithMstrInEconomics.length > 0 &&
          dataWithMstrInEconomics
            .filter((item) => item.name.toUpperCase() !== MANAGEMENT_HR)
            .map((item) => {
              if (!item.isCollection) {
                return (
                  <SidebarItem
                    key={item.name}
                    {...item}
                    data-cy={item.name}
                    isRoot={true}
                    selectedName={selectedName}
                    setSelectedName={setSelectedName}
                    onNavigateStart={handleNavigateStart}
                  />
                );
              }
            })}
      </List>
      <Divider className={classes.divider} />
      <ListItem
        component="a"
        dense
        disableGutters
        href="mailto:cazcon@techint.com?subject=Reports"
        sx={{ width: "100%", maxWidth: "100%", pl: 0 }}
        style={{ cursor: "pointer" }}
      >
        <ListItemIcon style={{ color: "white", cursor: "pointer" }}>
          <ContactSupportIcon />
        </ListItemIcon>
        <ListItemText className={classes.text} primary={"Contact Support"} style={{ cursor: "pointer" }} />
      </ListItem>
      {(isAdmin || isManagementHR) && (
        <>
          <Divider className={classes.divider} />
          <ListItem
            dense
            disableGutters
            sx={{ width: "100%", maxWidth: "100%", pl: 0 }}
            style={{ cursor: "pointer" }}
            onClick={handleClickAdministration}
          >
            <ListItemIcon style={{ color: "white", cursor: "pointer" }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText className={classes.text} primary={"Administration"} style={{ cursor: "pointer" }} />
            {openAdministration && isSidebarOpen ? (
              <ExpandLessIcon className={classes.icon} />
            ) : (
              <ExpandMoreIcon className={classes.icon} />
            )}
          </ListItem>
        </>
      )}
      <Collapse
        unmountOnExit
        in={openAdministration && isSidebarOpen}
        style={{ marginLeft: 0, marginBottom: 20, minHeight: "auto" }}
        timeout={0}
      >
        <List
          disablePadding
          component="div"
          sx={{
            "& .sidebar-click-item": {
              marginLeft: 0,
              paddingLeft: 0,
            },
          }}
        >
          {isAdmin && (
            <>
              <ListItem
                className="sidebar-click-item"
                style={{ cursor: "pointer" }}
                onClick={handleRedirect("/Administration/Filter")}
              >
                <ListItemIcon style={{ color: "white" }}>
                  <FilterListIcon />
                </ListItemIcon>
                <ListItemText className={classes.text} primary={"Filter"} />
              </ListItem>
              <ListItem
                className="sidebar-click-item"
                style={{ cursor: "pointer" }}
                onClick={handleRedirect("/Administration/Layout")}
              >
                <ListItemIcon style={{ color: "white" }}>
                  <ViewQuiltIcon />
                </ListItemIcon>
                <ListItemText className={classes.text} primary={"Layout"} />
              </ListItem>
              <ListItem
                className="sidebar-click-item"
                style={{ cursor: "pointer" }}
                onClick={handleRedirect("/Administration/MenuItem")}
              >
                <ListItemIcon style={{ color: "white" }}>
                  <ViewListIcon />
                </ListItemIcon>
                <ListItemText className={classes.text} primary={"MenuItem"} />
              </ListItem>
              <ListItem
                className="sidebar-click-item"
                style={{ cursor: "pointer" }}
                onClick={handleRedirect("/Administration/Source")}
              >
                <ListItemIcon style={{ color: "white" }}>
                  <AspectRatioIcon />
                </ListItemIcon>
                <ListItemText className={classes.text} primary={"Source"} />
              </ListItem>
              <ListItem
                className="sidebar-click-item"
                style={{ cursor: "pointer" }}
                onClick={handleRedirect("/Administration/Groups")}
              >
                <ListItemIcon style={{ color: "white" }}>
                  <SupervisorAccountIcon />
                </ListItemIcon>
                <ListItemText className={classes.text} primary={"Groups"} />
              </ListItem>
              <ListItem
                className="sidebar-click-item"
                style={{ cursor: "pointer" }}
                onClick={handleRedirect("/Administration/Analytics")}
              >
                <ListItemIcon style={{ color: "white" }}>
                  <TimelineIcon />
                </ListItemIcon>
                <ListItemText className={classes.text} primary={"Analytics Usage"} />
              </ListItem>
            </>
          )}
          {(isAdmin || isManagementHR) && (
            <>
              <ListItem
                className="sidebar-click-item"
                style={{ cursor: "pointer" }}
                onClick={handleRedirect("/Administration/Hr")}
              >
                <ListItemIcon style={{ color: "white" }}>
                  <AssignmentIcon />
                </ListItemIcon>
                <ListItemText className={classes.text} primary={"Management HR"} />
              </ListItem>
            </>
          )}
          {isAdmin && (
            <>
              <ListItem
                className="sidebar-click-item"
                style={{ cursor: "pointer" }}
                onClick={handleRedirect("/SearchUser")}
              >
                <ListItemIcon style={{ color: "white" }}>
                  <SearchIcon />
                </ListItemIcon>
                <ListItemText className={classes.text} primary={"Search Users"} />
              </ListItem>
            </>
          )}
        </List>
      </Collapse>
      <img
        alt="logoTDA"
        height={45}
        src={`${process.env.PUBLIC_URL}/Logo-TDA-blanco-verde.png`}
        style={{ margin: "auto", marginBottom: 10 }}
        width={"auto"}
      />
    </Drawer>
  );
}
